import React from "react";
import { BRIEF_VIEW } from "../briefModel";
import "../../_shared/_qokr-buttons.scss";
import "./_brief-states.scss";

// Every brief state that isn't READY (handoff 34 §3). None of them blocks
// the coach: each carries "Open review" (or the quarter's own next step).

const SkeletonLines = ({ count = 4 }) => (
  <div className="qokr-brief-state__skeleton" aria-hidden="true">
    {Array.from({ length: count }).map((_, index) => (
      <div className="sc-skeleton-block scb-skeleton-line" key={index} />
    ))}
  </div>
);

const BriefStates = ({
  view,
  error,
  canRegenerate,
  regenerating,
  onRegenerate,
  onRetry,
  next, // { label, onClick } — the header CTA, repeated here
}) => {
  const nextButton = next ? (
    <button
      type="button"
      className="qokr-btn qokr-btn--primary"
      onClick={next.onClick}
    >
      {next.label}
    </button>
  ) : null;

  if (error) {
    return (
      <div className="scb-empty-card qokr-brief-state">
        <i
          className="fa-solid fa-circle-exclamation qokr-brief-state__icon qokr-brief-state__icon--warn"
          aria-hidden="true"
        />
        <div className="scb-empty-title">We couldn&apos;t load the brief</div>
        <div className="scb-empty-subtitle">{error}</div>
        <div className="qokr-brief-state__actions">
          <button
            type="button"
            className="qokr-btn qokr-btn--secondary"
            onClick={onRetry}
          >
            Try again
          </button>
          {nextButton}
        </div>
      </div>
    );
  }

  if (view === BRIEF_VIEW.LOADING) {
    return (
      <div className="scb-summary-card">
        <SkeletonLines count={6} />
      </div>
    );
  }

  if (view === BRIEF_VIEW.PREPARING_OKRS) {
    return (
      <div className="scb-empty-card qokr-brief-state">
        <span className="qokr-brief-state__spinner" aria-hidden="true" />
        <div className="scb-empty-title">
          Preparing your client&apos;s OKRs…
        </div>
        <div className="scb-empty-subtitle">
          This usually takes under a minute.
        </div>
      </div>
    );
  }

  if (view === BRIEF_VIEW.FAILED) {
    return (
      <div className="scb-empty-card qokr-brief-state">
        <i
          className="fa-solid fa-file-circle-exclamation qokr-brief-state__icon qokr-brief-state__icon--warn"
          aria-hidden="true"
        />
        <div className="scb-empty-title">Brief not available</div>
        <div className="scb-empty-subtitle">
          Something went wrong while writing the brief.
        </div>
        <div className="qokr-brief-state__actions">
          {canRegenerate ? (
            <button
              type="button"
              className="qokr-btn qokr-btn--secondary"
              disabled={regenerating}
              onClick={onRegenerate}
            >
              <i className="fa-solid fa-rotate-right" aria-hidden="true" />
              {regenerating ? "Regenerating…" : "Regenerate"}
            </button>
          ) : null}
          {nextButton}
        </div>
      </div>
    );
  }

  // null / IN_PROGRESS — the layout's skeleton under the notice.
  return (
    <div className="qokr-brief-state--preparing">
      <div className="qokr-brief-state__notice" role="status">
        <span className="qokr-brief-state__spinner qokr-brief-state__spinner--sm" />
        <p>
          Your brief is being prepared. You can start the review now. The brief
          will appear here when it&apos;s ready.
        </p>
        {nextButton}
      </div>
      <div className="scb-summary-card">
        <SkeletonLines count={3} />
      </div>
      <div className="scb-summary-card">
        <SkeletonLines count={5} />
      </div>
    </div>
  );
};

export default BriefStates;
